import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import type { EmailContext } from "../../email/types";
import {
  draftAgenda,
  findMeetingEmail,
  inviteAsEmail,
  type AgendaDraft,
  type AgendaMeeting,
} from "../services/agendaDraft";

/** What the agenda is drafted for: the meeting as the AI is told about it, plus who to treat as the thread's people. */
export interface AgendaDraftRequest extends AgendaMeeting {
  readonly organizerEmail: string;
  readonly attendeeEmails: readonly string[];
  readonly userDisplayName: string;
}

export type AgendaDraftState =
  | { readonly status: "idle" }
  /** `source`: what it is drafted from, for "AiVA is drafting from …". `text`: the agenda so far. */
  | { readonly status: "drafting"; readonly source: string; readonly text: string }
  | { readonly status: "failed"; readonly message: string };

/**
 * Draft With AiVA for one meeting: finds the meeting's email thread, asks AiVA
 * for an agenda from it, and streams the agenda back. A draft still running is
 * cancelled when another starts or the meeting screen closes.
 */
export function useAgendaDraft() {
  const { apiToken, graphToken, refreshTokens, refreshGraphToken } = useAuthState();
  const [state, setState] = useState<AgendaDraftState>({ status: "idle" });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const start = useCallback(
    async (request: AgendaDraftRequest): Promise<(AgendaDraft & { readonly source: string }) | null> => {
      const backendToken = apiToken ?? graphToken;
      if (!backendToken || !graphToken) {
        setState({ status: "failed", message: "Sign in to AiVA to draft an agenda." });
        return null;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setState({ status: "drafting", source: "your email", text: "" });

      let email: EmailContext | null = null;
      try {
        email = await findMeetingEmail(graphToken, () => refreshGraphToken(false), request.subject);
      } catch (e: unknown) {
        // No thread to draft from is not a failure: the invite is used instead.
        logConnectorFailure("calendar", e, "find-meeting-email");
      }
      if (controller.signal.aborted) return null;
      const source = email ? `the "${email.subject}" email thread` : "the invite";
      setState({ status: "drafting", source, text: "" });

      try {
        const draft = await draftAgenda({
          apiToken: backendToken,
          refresh: refreshTokens,
          email:
            email ??
            inviteAsEmail({
              subject: request.subject,
              organizerEmail: request.organizerEmail,
              attendees: request.attendeeEmails,
              inviteText: request.inviteText,
            }),
          meeting: {
            subject: request.subject,
            durationLabel: request.durationLabel,
            location: request.location,
            organizerName: request.organizerName,
            attendeeNames: request.attendeeNames,
            inviteText: request.inviteText,
            notes: request.notes,
          },
          userDisplayName: request.userDisplayName,
          signal: controller.signal,
          onText: (text) => {
            if (!controller.signal.aborted) setState({ status: "drafting", source, text });
          },
        });
        if (controller.signal.aborted) return null;
        setState({ status: "idle" });
        logConnectorSuccess("calendar", "draft-agenda");
        return { ...draft, source };
      } catch (e: unknown) {
        if (controller.signal.aborted) return null;
        setState({ status: "failed", message: e instanceof Error ? e.message : "AiVA couldn't draft the agenda." });
        logConnectorFailure("calendar", e, "draft-agenda");
        return null;
      }
    },
    [apiToken, graphToken, refreshTokens, refreshGraphToken],
  );

  return { state, start };
}
